import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";

export default function VideoShowcase() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [unsupported, setUnsupported] = useState(false);

  const toggle = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      try {
        await v.play();
        setPlaying(true);
      } catch (err) {
        // Codec/decode not supported on this client — fail gracefully, never crash.
        setUnsupported(true);
        setPlaying(false);
      }
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section id="in-motion" className="relative py-24 md:py-32 overflow-hidden" data-testid="video-showcase">
      <div className="glow-orb w-[500px] h-[500px] bg-neon-cyan/12 top-0 right-0" />
      <div className="container-x relative z-10">
        <Reveal className="max-w-2xl">
          <p className="overline mb-4">See it in motion</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-none">
            This is GateMate, <span className="text-gradient">for real</span>.
          </h2>
          <p className="mt-5 text-lg text-zinc-400 leading-relaxed">
            A first look at the actual app — the gate opens, the room lights up, and the people around you are one tap away.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-8 items-center">
          {/* Video */}
          <Reveal delay={0.1} className="lg:col-span-8">
            <div className="relative group rounded-3xl overflow-hidden glass glass-cyan" data-testid="video-frame">
              <video
                ref={videoRef}
                poster="/generated/showcase-poster.webp"
                playsInline
                webkit-playsinline="true"
                x-webkit-airplay="allow"
                loop
                muted
                controls={playing}
                controlsList="nodownload"
                preload="none"
                onClick={toggle}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onError={() => setUnsupported(true)}
                className="w-full h-auto block cursor-pointer aspect-video object-cover bg-black"
              >
                <source src="/brand/GateMateIntro.mp4" type="video/mp4" />
                <source src="/brand/GateMateIntro.webm" type="video/webm" />
              </video>
              <button
                onClick={toggle}
                data-testid="video-play-button"
                aria-label={playing ? "Pause video" : "Play video"}
                className={`absolute inset-0 grid place-items-center transition-opacity duration-300 ${
                  playing ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}
              >
                <span className="grid place-items-center w-20 h-20 rounded-full bg-black/50 backdrop-blur-md border border-white/20 text-white shadow-[0_0_40px_rgba(0,198,174,0.4)] transition-transform hover:scale-110">
                  {playing ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                </span>
              </button>
              {unsupported && (
                <div className="absolute bottom-3 left-3 right-3 text-center text-xs text-zinc-400 bg-black/60 rounded-lg py-1.5" data-testid="video-unsupported">
                  Your browser can't play this clip — <a href="/brand/GateMateIntro.mp4" className="text-neon-cyan underline">open it directly</a>.
                </div>
              )}
            </div>
          </Reveal>

          {/* Real welcome screen — cinematic device treatment */}
          <Reveal delay={0.2} className="lg:col-span-4">
            <div className="relative mx-auto max-w-[280px]" style={{ perspective: "1000px" }}>
              <div className="glow-orb w-[260px] h-[260px] bg-neon-cyan/30 top-1/4 left-1/2 -translate-x-1/2" />
              <div className="glow-orb w-[220px] h-[220px] bg-neon-violet/25 bottom-0 left-1/2 -translate-x-1/2" />
              <motion.div
                animate={{ y: [0, -14, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                style={{ transform: "rotateY(-10deg) rotateX(3deg)" }}
                className="relative z-10"
              >
                <div className="relative rounded-[2.6rem] p-2 bg-gradient-to-br from-neon-cyan/50 via-white/5 to-neon-violet/50 shadow-[0_35px_80px_rgba(0,198,174,0.35)]">
                  <div className="rounded-[2.2rem] overflow-hidden bg-black border border-white/10">
                    <img
                      src="/brand/app-screen-welcome.webp"
                      alt="GateMate app welcome and sign-in screen"
                      loading="lazy"
                      className="w-full block"
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0 rounded-[2.6rem] bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
                </div>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
